import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import PageContainer from '../view/page-container';
import { SectionInner } from '../components/section/common';
import { pretendardFont } from '../styles/typography';
import {
  downloadImageFromUrl,
  fetchMicrolinkMetadata,
  getMediaUrl,
  isValidHttpUrl,
  type MicrolinkData,
  type MicrolinkResponse,
} from '../utils/microlink';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding: 140px 0 120px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 120px 0 80px;
  }
`;

const PageTitle = styled.h1`
  ${pretendardFont}
  color: #111111;
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -1px;
  margin-bottom: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 28px;
  }
`;

const PageDescription = styled.p`
  ${pretendardFont}
  color: #555555;
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const InputRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const UrlInput = styled.input`
  ${pretendardFont}
  flex: 1;
  height: 52px;
  padding: 0 16px;
  border: 1px solid #dddddd;
  border-radius: 12px;
  font-size: 16px;
  color: #111111;
  background: #ffffff;

  &:focus {
    outline: 2px solid #d3ee34;
    border-color: #111111;
  }
`;

const ApiKeyInput = styled(UrlInput)`
  height: 44px;
  font-size: 14px;
`;

const SubmitButton = styled.button`
  ${pretendardFont}
  height: 52px;
  padding: 0 24px;
  border: none;
  border-radius: 999px;
  background: #111111;
  color: #d3ee34;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.p<{ $error?: boolean }>`
  ${pretendardFont}
  color: ${({ $error }) => ($error ? '#d32f2f' : '#555555')};
  font-size: 14px;
  margin-bottom: 24px;
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 360px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const PreviewCard = styled.div`
  border: 1px solid #eeeeee;
  border-radius: 20px;
  overflow: hidden;
  background: #fafafa;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 1.91 / 1;
  object-fit: cover;
  background: #eeeeee;
`;

const PreviewImageFallback = styled.div`
  width: 100%;
  aspect-ratio: 1.91 / 1;
  background: #eeeeee;
`;

const PreviewBody = styled.div`
  padding: 20px;
`;

const PreviewTitle = styled.h2`
  ${pretendardFont}
  color: #111111;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 8px;
`;

const PreviewDescription = styled.p`
  ${pretendardFont}
  color: #555555;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 12px;
`;

const PreviewUrl = styled.a`
  ${pretendardFont}
  color: #9db510;
  font-size: 13px;
  word-break: break-all;
`;

const Panel = styled.div`
  border: 1px solid #eeeeee;
  border-radius: 20px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #eeeeee;
  background: #fafafa;
`;

const PanelTitle = styled.h3`
  ${pretendardFont}
  color: #111111;
  font-size: 16px;
  font-weight: 700;
`;

const CopyButton = styled.button`
  ${pretendardFont}
  padding: 8px 14px;
  border: 1px solid #111111;
  border-radius: 999px;
  background: #ffffff;
  color: #111111;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.span`
  ${pretendardFont}
  color: #888888;
  font-size: 13px;
  font-weight: 600;
`;

const FieldValue = styled.pre`
  ${pretendardFont}
  margin: 0;
  color: #111111;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
`;

const JsonBlock = styled.pre`
  ${pretendardFont}
  margin: 0;
  padding: 20px;
  max-height: 420px;
  overflow: auto;
  background: #111111;
  color: #d3ee34;
  font-size: 13px;
  line-height: 1.6;
`;

const DEFAULT_API_KEY = process.env.REACT_APP_MICROLINK_API_KEY ?? '';

const flattenMetadataFields = (data: MicrolinkData) => {
  const fields: Array<{ key: string; value: string; mediaUrl?: string }> = [];

  const push = (key: string, value: unknown) => {
    if (value == null || value === '') return;
    if (typeof value === 'object') {
      fields.push({ key, value: JSON.stringify(value, null, 2) });
      return;
    }
    fields.push({ key, value: String(value) });
  };

  const pushMedia = (key: string, value: unknown) => {
    if (value == null || value === '') return;
    const mediaUrl = getMediaUrl(value as MicrolinkData['image']);
    if (!mediaUrl) return;

    fields.push({
      key,
      value: mediaUrl,
      mediaUrl,
    });
  };

  push('title', data.title);
  push('description', data.description);
  push('url', data.url);
  push('lang', data.lang);
  push('author', data.author);
  push('publisher', data.publisher);
  push('date', data.date);
  pushMedia('image', data.image);
  pushMedia('logo', data.logo);
  push('video', data.video);

  return fields;
};

const getPrimaryImageUrl = (data?: MicrolinkData) =>
  getMediaUrl(data?.image) ?? getMediaUrl(data?.logo);

const TestPage: React.FC = () => {
  const [inputUrl, setInputUrl] = useState('');
  const [apiKey, setApiKey] = useState(DEFAULT_API_KEY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [response, setResponse] = useState<MicrolinkResponse | null>(null);
  const [copiedKey, setCopiedKey] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastDownloadedImageRef = useRef('');

  const data = response?.data;
  const fields = useMemo(
    () => (data ? flattenMetadataFields(data) : []),
    [data],
  );
  const jsonText = useMemo(
    () => (data ? JSON.stringify(data, null, 2) : ''),
    [data],
  );

  const copyText = useCallback(async (text: string, key: string) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey(''), 1500);
  }, []);

  const copyField = useCallback(
    async (field: { key: string; value: string; mediaUrl?: string }) => {
      await copyText(field.value, field.key);

      if (field.mediaUrl) {
        await downloadImageFromUrl(field.mediaUrl);
      }
    },
    [copyText],
  );

  const downloadPrimaryImage = useCallback(async (result: MicrolinkResponse) => {
    const imageUrl = getPrimaryImageUrl(result.data);
    if (!imageUrl || imageUrl === lastDownloadedImageRef.current) {
      return;
    }

    lastDownloadedImageRef.current = imageUrl;
    await downloadImageFromUrl(imageUrl);
  }, []);

  const fetchMetadata = useCallback(
    async (rawUrl: string) => {
      if (!isValidHttpUrl(rawUrl)) {
        setError('올바른 URL을 입력해 주세요.');
        setResponse(null);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const result = await fetchMicrolinkMetadata(rawUrl, apiKey || undefined);
        setResponse(result);
        await downloadPrimaryImage(result);
      } catch (err) {
        setResponse(null);
        lastDownloadedImageRef.current = '';
        setError(
          err instanceof Error ? err.message : '메타데이터 조회에 실패했습니다.',
        );
      } finally {
        setLoading(false);
      }
    },
    [apiKey, downloadPrimaryImage],
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!inputUrl.trim() || !isValidHttpUrl(inputUrl)) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchMetadata(inputUrl);
    }, 700);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputUrl, fetchMetadata]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    fetchMetadata(inputUrl);
  };

  const previewImage = getPrimaryImageUrl(data);

  return (
    <PageContainer>
      <PageWrapper>
        <SectionInner>
          <PageTitle>메타데이터 테스트</PageTitle>
          <PageDescription>
            URL을 입력하면 Microlink API로 메타정보를 조회합니다. 이미지 URL이
            있으면 자동으로 다운로드되며, 필드별 또는 JSON 전체를 복사할 수
            있습니다.
          </PageDescription>

          <Form onSubmit={handleSubmit}>
            <InputRow>
              <UrlInput
                type="url"
                value={inputUrl}
                onChange={(event) => setInputUrl(event.target.value)}
                placeholder="https://github.com/microlinkhq"
                aria-label="조회할 URL"
              />
              <SubmitButton type="submit" disabled={loading || !inputUrl.trim()}>
                {loading ? '조회 중…' : '조회'}
              </SubmitButton>
            </InputRow>
            <ApiKeyInput
              type="password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="Microlink API Key (선택, PRO)"
              aria-label="Microlink API Key"
            />
          </Form>

          {loading && <StatusMessage>메타데이터를 불러오는 중입니다…</StatusMessage>}
          {error && <StatusMessage $error>{error}</StatusMessage>}
          {!loading && !error && data && (
            <StatusMessage>
              조회 완료 — 이미지가 있으면 자동 다운로드됩니다. 아래에서 확인하고
              복사하세요.
            </StatusMessage>
          )}

          {data && (
            <ResultGrid>
              <PreviewCard>
                {previewImage ? (
                  <PreviewImage src={previewImage} alt={data.title ?? 'preview'} />
                ) : (
                  <PreviewImageFallback aria-hidden />
                )}
                <PreviewBody>
                  <PreviewTitle>{data.title ?? '제목 없음'}</PreviewTitle>
                  <PreviewDescription>
                    {data.description ?? '설명 없음'}
                  </PreviewDescription>
                  {data.url && (
                    <PreviewUrl href={data.url} target="_blank" rel="noreferrer">
                      {data.url}
                    </PreviewUrl>
                  )}
                </PreviewBody>
              </PreviewCard>

              <div>
                <Panel>
                  <PanelHeader>
                    <PanelTitle>필드별 복사</PanelTitle>
                    <CopyButton
                      type="button"
                      onClick={() => copyText(jsonText, 'all-fields')}
                    >
                      {copiedKey === 'all-fields' ? '복사됨' : '전체 JSON 복사'}
                    </CopyButton>
                  </PanelHeader>
                  <FieldList>
                    {fields.map((field) => (
                      <FieldRow key={field.key}>
                        <FieldLabel>{field.key}</FieldLabel>
                        <FieldValue>{field.value}</FieldValue>
                        <CopyButton
                          type="button"
                          onClick={() => copyField(field)}
                        >
                          {copiedKey === field.key ? '복사됨' : '복사'}
                        </CopyButton>
                      </FieldRow>
                    ))}
                  </FieldList>
                </Panel>

                <Panel style={{ marginTop: 24 }}>
                  <PanelHeader>
                    <PanelTitle>JSON 응답 (data)</PanelTitle>
                    <CopyButton
                      type="button"
                      onClick={() => copyText(jsonText, 'json')}
                    >
                      {copiedKey === 'json' ? '복사됨' : 'JSON 복사'}
                    </CopyButton>
                  </PanelHeader>
                  <JsonBlock>{jsonText}</JsonBlock>
                </Panel>
              </div>
            </ResultGrid>
          )}
        </SectionInner>
      </PageWrapper>
    </PageContainer>
  );
};

export default TestPage;
